import { useTranslation } from 'react-i18next';
import './UmpireTrendsChart.scss';
import { ReactComponent as BasePlateDown } from 'svg/BasePlateDown.svg';

import { UmpireTrendDataDto, UmpireTrendsChartConfigDto, UmpireTrendsChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { Typography } from '@trackman/web-shared-components';

type HeightLocation = 'Below' | 'Bottom' | 'Middle' | 'Top' | 'Above';
type SideLocation = 'Outside' | 'Outer' | 'Middle' | 'Inner' | 'Inside';

type UmpireTrendProps = {
  data: UmpireTrendDataDto | undefined;
};

function getLocationPosition(heightLocation: HeightLocation, sideLocation: SideLocation) {
  const top = new Map<HeightLocation, number>([
    ['Below', 100],
    ['Bottom', 66],
    ['Middle', 33],
    ['Top', 0],
    ['Above', -33],
  ]).get(heightLocation);

  const left = new Map<SideLocation, number>([
    ['Outside', -33],
    ['Outer', 0],
    ['Middle', 33],
    ['Inner', 66],
    ['Inside', 100],
  ]).get(sideLocation);

  return {
    top: top + '%',
    left: left + '%',
  };
}

const UmpireTrend = ({ data }: UmpireTrendProps) => {
  const { t } = useTranslation();

  return (
    <div className='UmpireTrend'>
      <div className='UmpireTrend-strike-zone-container'>
        <div className='UmpireTrend-strike-zone-rect'>
          <div
            className='UmpireTrend-strike-zone-location-area'
            style={
              data == null
                ? { display: 'none' }
                : {
                    ...getLocationPosition(data.heightLocation as HeightLocation, data.sideLocation as SideLocation),
                  }
            }
          ></div>
        </div>
        <div className='UmpireTrend-strike-zone-bottom-space'></div>
        <BasePlateDown className='UmpireTrend-strike-zone-base-plate' />
      </div>
      <div className='UmpireTrend-metric'>
        <Typography variant='label' color='#A0A0A0'>
          {t('Height | Side')}
        </Typography>
        <Typography>{data == null ? '-' : `${t(data.heightLocation)} | ${t(data.sideLocation)}`}</Typography>
      </div>
      <div className='UmpireTrend-metric'>
        <Typography variant='label' color='#A0A0A0'>
          {t('Accuracy')}
        </Typography>
        <div>
          <Typography variant='h1' component='span'>
            {data == null ? '-' : Math.round(data.accuracy.percentage)} %
          </Typography>
          <Typography component='span'>{data == null ? '= -/-' : ` = ${data.accuracy.score} / ${data.accuracy.total}`}</Typography>
        </div>
      </div>
    </div>
  );
};

const UmpireTrendsChart = ({ chartData, isLoading, chartConfig }: ChartProps<UmpireTrendsChartDataDto, UmpireTrendsChartConfigDto>) => {
  const chartDataDisplay = chartData?.umpireTrends ?? [...Array(chartConfig.numberOfTilesInModule)];

  return (
    <div className='UmpireTrendsChart'>
      {chartDataDisplay.map((trend, index) => (
        <UmpireTrend key={index} data={trend} />
      ))}
    </div>
  );
};

export default UmpireTrendsChart;
